import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { LogOut, Trash2, Eye } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const STATUS_STYLES = {
  pending: "bg-[hsl(38,80%,92%)] text-[hsl(28,80%,32%)] border-[hsl(28,60%,60%)]",
  confirmed: "bg-[hsl(150,40%,90%)] text-[hsl(150,50%,25%)] border-[hsl(150,35%,55%)]",
  completed: "bg-muted text-secondary border-border",
  rejected: "bg-destructive/10 text-destructive border-destructive/40",
};

const FILTERS = ["all", "pending", "confirmed", "completed", "rejected"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, completed: 0, rejected: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, s] = await Promise.all([
        api.get("/orders", { params: filter === "all" ? {} : { status: filter } }),
        api.get("/orders/stats"),
      ]);
      setOrders(o.data);
      setStats(s.data);
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        navigate("/admin/login");
      } else toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [filter, logout, navigate]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/orders/${id}/status`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/orders/${id}`);
      toast.success("Order deleted");
      setSelected(null);
      load();
    } catch { toast.error("Delete failed"); }
  };

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const statCards = [
    { label: "Total", value: stats.total, key: "all" },
    { label: "Pending", value: stats.pending, key: "pending" },
    { label: "Confirmed", value: stats.confirmed, key: "confirmed" },
    { label: "Completed", value: stats.completed, key: "completed" },
  ];

  return (
    <div className="min-h-screen bg-background text-primary relative">
      <div className="noise-overlay" />
      <header className="relative z-10 flex items-center justify-between px-6 sm:px-10 py-6 border-b border-border">
        <div>
          <span className="font-serif text-2xl">CakesbyaKid</span>
          <span className="text-secondary text-sm ml-3 hidden sm:inline">Order Book</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-secondary hidden sm:inline" data-testid="admin-email">{user?.email}</span>
          <Button onClick={doLogout} variant="outline" data-testid="logout-button" className="rounded-sm border-border h-10 gap-2">
            <LogOut size={15} /> Logout
          </Button>
        </div>
      </header>

      <main className="relative z-10 px-6 sm:px-10 py-10 max-w-7xl">
        <h1 className="font-serif text-4xl sm:text-5xl mb-8">Incoming requests.</h1>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-10">
          {statCards.map((s) => (
            <button
              key={s.key}
              onClick={() => setFilter(s.key)}
              data-testid={`stat-${s.key}`}
              className={`text-left bg-white border rounded-sm p-6 transition-colors ${filter === s.key ? "border-accent" : "border-border hover:border-secondary"}`}
            >
              <p className="text-xs uppercase tracking-widest text-secondary">{s.label}</p>
              <p className="font-serif text-4xl mt-2">{s.value}</p>
            </button>
          ))}
        </div>

        {/* Filter */}
        <div className="flex flex-wrap gap-2 mb-6">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
              className={`px-4 py-2 rounded-sm text-sm capitalize transition-colors ${filter === f ? "bg-primary text-primary-foreground" : "bg-white border border-border text-secondary hover:text-primary"}`}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white border border-border rounded-sm overflow-hidden">
          {loading ? (
            <p className="p-10 text-center text-secondary">Loading orders…</p>
          ) : orders.length === 0 ? (
            <p className="p-10 text-center text-secondary" data-testid="empty-state">No orders in this view yet.</p>
          ) : (
            <Table data-testid="orders-table">
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-secondary">Customer</TableHead>
                  <TableHead className="text-secondary hidden md:table-cell">Cake</TableHead>
                  <TableHead className="text-secondary hidden lg:table-cell">Occasion</TableHead>
                  <TableHead className="text-secondary">Pickup</TableHead>
                  <TableHead className="text-secondary">Status</TableHead>
                  <TableHead className="text-secondary text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((o) => (
                  <TableRow key={o._id} className="border-border" data-testid={`order-row-${o._id}`}>
                    <TableCell>
                      <p className="font-medium">{o.customer_name}</p>
                      <p className="text-xs text-secondary">{o.email}</p>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-secondary">{o.flavor}<br /><span className="text-xs">{o.size}</span></TableCell>
                    <TableCell className="hidden lg:table-cell text-secondary">{o.occasion}</TableCell>
                    <TableCell className="text-secondary">{fmtDate(o.delivery_date)}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Badge variant="outline" className={`capitalize rounded-sm w-fit ${STATUS_STYLES[o.status] || ""}`}>{o.status}</Badge>
                        {o.payment_status === "deposit_paid" && (
                          <span className="text-[10px] uppercase tracking-wide text-[hsl(150,55%,30%)]" data-testid={`deposit-${o._id}`}>$25 deposit paid</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <Select value={o.status} onValueChange={(v) => changeStatus(o._id, v)}>
                          <SelectTrigger data-testid={`status-select-${o._id}`} className="h-9 w-[130px] bg-white border-border rounded-sm text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-border">
                            {["pending", "confirmed", "completed", "rejected"].map((s) => (
                              <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button size="icon" variant="ghost" onClick={() => setSelected(o)} data-testid={`view-${o._id}`} className="h-9 w-9 rounded-sm">
                          <Eye size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-white border-border max-w-lg max-h-[85vh] overflow-y-auto" data-testid="order-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-3xl">{selected.customer_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <Detail label="Contact" value={`${selected.email} · ${selected.phone}`} />
                <div className="grid grid-cols-2 gap-4">
                  <Detail label="Flavor" value={selected.flavor} />
                  <Detail label="Size" value={selected.size} />
                  <Detail label="Occasion" value={selected.occasion} />
                  <Detail label="Frosting" value={selected.frosting} />
                </div>
                <Detail label="Pickup date" value={fmtDate(selected.delivery_date)} />
                <Detail label="Deposit" value={selected.payment_status === "deposit_paid" ? "Paid — $25 (balance due cash on pickup)" : "Not paid"} />
                <Detail label="Allergies" value={selected.allergies || "None noted"} />
                <Detail label="Special instructions" value={selected.special_instructions || "None"} />
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <Badge variant="outline" className={`capitalize rounded-sm ${STATUS_STYLES[selected.status] || ""}`}>{selected.status}</Badge>
                  <Button onClick={() => remove(selected._id)} variant="outline" data-testid="delete-order-button" className="rounded-sm border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive gap-2">
                    <Trash2 size={15} /> Delete order
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-secondary">{label}</p>
      <p className="text-primary mt-1 leading-relaxed">{value}</p>
    </div>
  );
}

function fmtDate(d) {
  try { return format(new Date(d), "MMM d, yyyy"); } catch { return d; }
}
